/**
 * WHORL LOADER — ported from the aesthetic reference (§14, L3634–3647). Owner: S3.
 *
 * The tree rings draw from the centre out as the land is prepared: `progress`
 * 0..1 goes straight to <Whorl progress/>, so byte-accurate progress reads as
 * rings. Below the figure: the label, an optional detail line ("8.4 of 12.1 MB"),
 * and an optional action slot that appears once progress reaches 1 (for
 * example an "Enter" button). `done` fades the whole layer out (css/overlays.css).
 *
 * Accessibility: the rings, figure and text form one role="progressbar". The
 * action sits outside it, because a progressbar's children are presentational
 * and a button inside one would be hidden from assistive tech.
 */
import type { CSSProperties, JSX, ReactNode } from "react";
import { Whorl } from "./whorl";
import { Button } from "./primitives";

const cx = (...a: Array<string | false | null | undefined>): string => a.filter(Boolean).join(" ");
const clamp01 = (v: number): number => (Number.isFinite(v) ? (v < 0 ? 0 : v > 1 ? 1 : v) : 0);

export interface WhorlLoaderProps {
  progress: number;
  done?: boolean;
  label?: string;
  detail?: ReactNode;
  action?: ReactNode; // e.g. an "Enter" button once ready
  style?: CSSProperties;
  /** Shorthand for `action`: a Sun button that appears when progress reaches 1. Ignored if `action` is given. */
  onEnter?: () => void;
  /** Label for the onEnter button. Default "Enter". */
  enterLabel?: string;
  /** Size of the mark in px. Default 112, as in the reference. */
  size?: number;
  className?: string;
}

const PANEL: CSSProperties = { display: "flex", flexDirection: "column", alignItems: "center", gap: 22 };
const DETAIL: CSSProperties = { marginTop: 8, font: "400 12.5px/1.4 var(--f-text)", color: "rgba(244,232,218,.5)" };
// Reserve the slot's height whenever an action can appear, so the rings do not jump when it does.
const SLOT: CSSProperties = { minHeight: 48, display: "grid", placeItems: "center" };

export function WhorlLoader({
  progress,
  done = false,
  label = "Preparing the map",
  detail,
  action,
  style,
  onEnter,
  enterLabel = "Enter",
  size = 112,
  className,
}: WhorlLoaderProps): JSX.Element {
  const p = clamp01(progress);
  const pct = Math.round(p * 100);
  const ready = p >= 1;
  const act = action ?? (onEnter ? <Button variant="sun" onClick={onEnter}>{enterLabel}</Button> : null);
  const valueText = typeof detail === "string" || typeof detail === "number" ? `${pct} percent, ${detail}` : `${pct} percent`;
  return (
    <div className={cx("ld-loader", done && "is-done", className)} style={style} aria-hidden={done || undefined} inert={done || undefined}>
      <div className="ld-loader__inner">
        <div
          role="progressbar"
          aria-label={label}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={pct}
          aria-valuetext={valueText}
          style={PANEL}
        >
          <Whorl size={size} progress={p} decorative />
          <div>
            <div className="ld-fig ld-num" style={{ color: "var(--ivory)" }}>
              {pct}
              <small>%</small>
            </div>
            <div className="ld-meta" style={{ marginTop: 12, color: "rgba(244,232,218,.6)" }}>
              {label}
            </div>
            {detail != null && detail !== false && (
              <div className="ld-num" style={DETAIL}>
                {detail}
              </div>
            )}
          </div>
        </div>
        {act && (
          <div style={SLOT}>
            {ready && (
              <div className="ld-in" style={{ "--fy": "8px" } as CSSProperties}>
                {act}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
